import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {QUEST_TYPES, QUESTS, checkIfQuestCompleted, getQuestIconPath} from '../../../configs/quests';
import {getTutorialChapterReward} from '../../../utils/tutorial/getTutorialChapterReward';
import {MissionRewardBadge} from '../TutorialMissionsPage/MissionRewardBadge';
import CampaignInDevelopmentModal from '../../Modals/CampaignInDevelopmentModal/CampaignInDevelopmentModal';
import '../TutorialMissionsPage/TutorialMissionsPage.css';
import './QuestsPage.css';

const PAGE_ID = 'QuestsPage';

// Статусный значок слева, как у плашек глав обучения: выполнено — галочка,
// заблокировано — замок, иначе — своя метка задания (у рейтинговых заданий —
// иконка лиги, см. getQuestIconPath)
const QuestStatusIcon = ({quest, isCompleted, isLocked}) => {
  const questIconPath = getQuestIconPath(quest);

  return (
    <span className={`tutorial-missions-page_mission-status${
      !isCompleted && !isLocked && questIconPath ? ' quests-page_league-icon' : ''
    }`}>
      {isCompleted && <img src={'/img/icons/check.png'} alt={'Выполнено'}/>}
      {!isCompleted && isLocked && <img src={'/img/icons/lock_grey.png'} alt={'Недоступно'}/>}
      {!isCompleted && !isLocked && (
        <img
          src={questIconPath || '/img/icons/rules_white.png'}
          alt={'Не выполнено'}
        />
      )}
    </span>
  );
};

/**
 * Плашка задания — та же кнопка, что у главы обучения на странице кампаний
 * (общие классы tutorial-missions-page_mission*): статус, название,
 * описание, дополнительные условия и блок награды.
 */
const QuestButton = ({quest, isCompleted, reward, rewardTitle, onClick}) => (
  <PageButton
    pageId={PAGE_ID}
    buttonId={`quest_${quest.id}`}
    className={'tutorial-missions-page_mission'}
    onClick={onClick}
    title={quest.description}
  >
    <span className={'tutorial-missions-page_mission-main'}>
      <QuestStatusIcon quest={quest} isCompleted={isCompleted} isLocked={quest.isLocked}/>
      <span className={'tutorial-missions-page_mission-info'}>
        <span className={'tutorial-missions-page_mission-title'}>{quest.title}</span>
        <span className={'tutorial-missions-page_mission-description'}>{quest.description}</span>
        {(quest.conditions || []).map((condition) => (
          <span key={condition} className={'quests-page_condition'}>{condition}</span>
        ))}
      </span>
    </span>
    <MissionRewardBadge reward={reward} title={rewardTitle}/>
  </PageButton>
);

/**
 * Страница «Задания»: сверху доступные (невыполненные), под ними —
 * выполненные с фактически полученными наградами (сервер запоминает, что
 * именно выдал, см. user.questRewards).
 */
const QuestsPage = ({onStartChapter, onLocalGame, onOnlineGame, onRankedGame, onShop, onBack}) => {
  usePageAnalytics(PAGE_ID);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);

  const completedTutorialChapters = useSelector(
    (state) => state.private.auth.completedTutorialChapters
  );
  const completedQuests = useSelector((state) => state.private.auth.completedQuests);
  const questRewards = useSelector((state) => state.private.auth.questRewards);
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);
  const isAllFactionsAvailable = useSelector((state) => state.private.auth.isAllFactionsAvailable);

  const ownedFactionIds = useMemo(
    () => (availableFactions || []).filter((faction) => faction.isAvailable).map((faction) => faction.id),
    [availableFactions]
  );

  const progress = useMemo(
    () => ({
      completedTutorialChapters: completedTutorialChapters || [],
      completedQuests: completedQuests || [],
    }),
    [completedTutorialChapters, completedQuests]
  );

  // Что реально выдали за уже выполненное задание: сервер запомнил награду в
  // момент выдачи. Для заданий, выполненных до появления этой записи, —
  // расчет по конфигу (та же награда, если фракция с тех пор не менялась)
  const getGrantedReward = (quest) => {
    const stored = (questRewards || []).find((item) => item.questId === quest.id);
    return stored?.reward || getTutorialChapterReward(quest, {ownedFactionIds, isAllFactionsAvailable});
  };

  const handleQuestClick = (quest) => {
    if (quest.isLocked) {
      setIsCampaignModalOpen(true);
      return;
    }
    if (quest.type === QUEST_TYPES.TUTORIAL_CHAPTER) {
      onStartChapter(quest.chapterId);
      return;
    }
    // Бой с ботами — локальная партия: ведем туда же, куда кнопка «Против
    // ботов» в главном меню, к выбору поля
    if (quest.type === QUEST_TYPES.LOCAL_MATCH) {
      onLocalGame();
      return;
    }
    if (quest.type === QUEST_TYPES.ONLINE_MATCH) {
      onOnlineGame();
      return;
    }
    // Задания про рейтинг (первый рейтинговый матч и подъём по лигам) ведут
    // туда же, куда кнопка «Рейтинговый матч» в главном меню — в поиск
    // рейтинговой партии
    if (quest.type === QUEST_TYPES.RANKED_MATCH || quest.type === QUEST_TYPES.LEAGUE) {
      onRankedGame();
    }
  };

  const availableQuests = QUESTS.filter((quest) => !checkIfQuestCompleted(quest, progress));
  const finishedQuests = QUESTS.filter((quest) => checkIfQuestCompleted(quest, progress));

  return (
    <>
      <PageButton
        className={'tutorial-missions-floating-back-button'}
        onClick={onBack}
        pageId={PAGE_ID}
        buttonId={'back_floating'}
      >
        <img
          className={'tutorial-missions-floating-back-button_icon'}
          src={'/img/icons/back_navigation.png'}
          alt={'Назад'}
          title={'Назад'}
        />
      </PageButton>

      <h1 className={'tutorial-missions-page_title'}>Задания</h1>

      {availableQuests.length > 0 && (
        <div className={'tutorial-missions-page_list'}>
          {availableQuests.map((quest) => (
            <QuestButton
              key={quest.id}
              quest={quest}
              isCompleted={false}
              reward={getTutorialChapterReward(quest, {ownedFactionIds, isAllFactionsAvailable})}
              onClick={() => handleQuestClick(quest)}
            />
          ))}
        </div>
      )}

      {finishedQuests.length > 0 && (
        <>
          <h2 className={'quests-page_subtitle'}>Выполненные</h2>
          <div className={'tutorial-missions-page_list quests-page_list_completed'}>
            {finishedQuests.map((quest) => (
              <QuestButton
                key={quest.id}
                quest={quest}
                isCompleted
                reward={getGrantedReward(quest)}
                rewardTitle={'Получено:'}
                onClick={() => handleQuestClick(quest)}
              />
            ))}
          </div>
        </>
      )}

      <div className={'main-menu-buttons'}>
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId={'back'}>
          Назад
        </PageButton>
      </div>

      {isCampaignModalOpen && (
        <CampaignInDevelopmentModal
          onShop={onShop}
          onClose={() => setIsCampaignModalOpen(false)}
        />
      )}
    </>
  );
};

export default QuestsPage;
